import { Vector2 } from './Vector2';

export class Verlet {
	public readonly dots: Dot[] = [];
	protected readonly sticks: Stick[] = [];

	public get numOfSticks() {
		return this.sticks.length;
	}

	public addDot(x: number, y: number, velX: number = 0, velY: number = 0): Dot {
		const dot = new Dot(x, y, velX, velY);
		this.dots.push(dot);
		return dot;
	}

	public addStick(dot1: number | Dot, dot2: number | Dot, length?: number) {
		if (typeof dot1 === 'number') dot1 = this.dots[dot1];
		if (typeof dot2 === 'number') dot2 = this.dots[dot2];
		if (!dot1 || !dot2) throw new Error('Dot not found');

		const stick = new Stick(dot1, dot2, length);
		this.sticks.push(stick);
		return stick;
	}

	public updateDots() {
		for (let i = 0; i < this.dots.length; i++) {
			this.dots[i].update();
		}
	}

	public updateSticks(deltaTime: number = 0.0166667) {
		for (let i = 0; i < this.sticks.length; i++) {
			this.sticks[i].update(deltaTime);
		}
	}

	public updateContrains() {
		for (let i = 0; i < this.dots.length; i++) {
			this.dots[i].constrain();
		}
	}
}

export class VerletTicker {
	private _isRendering = false;
	private _animationFrame: number = -1;

	private _iterations: number;
	private readonly verlets: Verlet[] = [];

	constructor(verlet: Verlet[] | Verlet, iterations: number = 16) {
		this.verlets = verlet instanceof Array ? verlet : [verlet];
		this._iterations = iterations;
	}

	public start() {
		if (this._isRendering) return;
		this._isRendering = true;
		this.tick();
	}

	public stop() {
		window.cancelAnimationFrame(this._animationFrame);
		this._isRendering = false;
	}

	private tick = () => {
		window.cancelAnimationFrame(this._animationFrame);
		if (!this._isRendering) return;
		this._animationFrame = window.requestAnimationFrame(this.tick);

		this.update();
	};

	public update(deltaTime: number = 0.0166667) {
		this.verlets.forEach((verlet) => verlet.updateDots());
		for (let j = 0; j < this._iterations; j++) {
			this.verlets.forEach((verlet) => verlet.updateSticks(deltaTime));
			this.verlets.forEach((verlet) => verlet.updateContrains());
		}
	}
}

export enum Boundaries {
	Top = 1,
	Right = 2,
	Bottom = 4,
	Left = 8
}

export class Dot {
	public pos: Vector2;
	public oldpos: Vector2;
	public friction: number = 0;
	public gravity: Vector2;

	public mass: number = 1;
	public velocity: Vector2;

	public pinned: boolean = false;

	public applyConstraints: boolean = true;
	public constrainEase: number = 1;

	private outOfBounds: number = 0;
	// private outOfBounds: boolean = false;
	public isOutOfBounds() {
		return !!this.outOfBounds;
	}
	public isOutOfBoundsLeft() {
		return !!(this.outOfBounds & Boundaries.Left);
	}
	public isOutOfBoundsRight() {
		return !!(this.outOfBounds & Boundaries.Right);
	}

	public contrains: { maxX: number; minX: number; maxY: number; minY: number } = {
		maxX: Number.MAX_SAFE_INTEGER,
		minX: Number.MIN_SAFE_INTEGER,
		maxY: Number.MAX_SAFE_INTEGER,
		minY: Number.MIN_SAFE_INTEGER
	};

	constructor(x: number = 0, y: number = 0, velX: number = 0, velY: number = 0) {
		this.pos = new Vector2(x, y);
		this.oldpos = new Vector2(x, y);
		this.velocity = new Vector2(velX, velY);
		this.friction = 0.97;
		this.gravity = new Vector2(0, 0);
		this.mass = 1;
	}

	public reset(x: number, y: number) {
		this.pos.x = x;
		this.pos.y = y;
		this.oldpos.x = x;
		this.oldpos.y = y;
	}

	public constrain() {
		if (!this.applyConstraints) return;

		this.outOfBounds = 0;

		if (this.pos.x > this.contrains.maxX) {
			const delta = this.pos.x - this.contrains.maxX;
			this.pos.x -= delta * this.constrainEase;
			this.outOfBounds = this.outOfBounds | Boundaries.Right;
		} else if (this.pos.x < this.contrains.minX) {
			const delta = this.contrains.minX - this.pos.x;
			this.pos.x += delta * this.constrainEase;
			this.outOfBounds = this.outOfBounds | Boundaries.Left;
		}

		// this.pos.x = Math.max(this.contrains.minX, Math.min(this.contrains.maxX, this.pos.x));
		// this.pos.y = Math.max(this.contrains.minY, Math.min(this.contrains.maxY, this.pos.y));
	}

	public update() {
		this.velocity = Vector2.sub(this.pos, this.oldpos);
		this.velocity.scale(this.friction);

		this.oldpos.set(this.pos);
		if (!this.pinned) {
			this.pos.add(this.velocity);
			this.pos.add(this.gravity);
		}
	}
}

export class Stick {
	public stiffness: number;
	public color: number;

	public startPoint: Dot;
	public endPoint: Dot;

	public length: number;

	public get magnitude() {
		const deltaX = this.startPoint.pos.x - this.endPoint.pos.x;
		const deltaY = this.startPoint.pos.y - this.endPoint.pos.y;
		return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
	}

	constructor(p1: Dot, p2: Dot, length?: number) {
		this.startPoint = p1;
		this.endPoint = p2;
		this.stiffness = 1;
		this.color = 0xf5476a;

		if (!length) {
			this.length = this.startPoint.pos.dist(this.endPoint.pos);
		} else {
			this.length = length;
		}
	}

	public update(deltaTime: number = 0.0166667) {
		// calculate the distance between two dots
		const dx = this.endPoint.pos.x - this.startPoint.pos.x;
		const dy = this.endPoint.pos.y - this.startPoint.pos.y;

		const deltaRatio = deltaTime * 60;

		// pythagoras theorem
		const dist = Math.sqrt(dx * dx + dy * dy);
		let diff = 0;
		if (dist > 0) {
			// calculate the resting distance betwen the dots
			diff = ((this.length - dist) / dist) * this.stiffness * deltaRatio;
		}

		// getting the offset of the points
		const offsetx = dx * diff * 0.5;
		const offsety = dy * diff * 0.5;

		// calculate mass
		let m1 = this.startPoint.mass + this.endPoint.mass;
		const m2 = this.startPoint.mass / m1;
		m1 = this.endPoint.mass / m1;

		// and finally apply the offset with calculated mass
		if (!this.startPoint.pinned) {
			this.startPoint.pos.x -= offsetx * m1;
			this.startPoint.pos.y -= offsety * m1;
		}
		if (!this.endPoint.pinned) {
			this.endPoint.pos.x += offsetx * m2;
			this.endPoint.pos.y += offsety * m2;
		}
	}
}
